/**
 * Full Survey Answer Graph Component
 * Displays all survey answers in one graph with anchor grouping
 * Kept separate from existing codebase.
 */
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Scatter, ScatterChart, ReferenceLine } from 'recharts';
import { getAnchorName, getAnchorColor } from '../../utils/anchorUtils';
import './FullSurveyAnswerGraph.css';

const FullSurveyAnswerGraph = ({ graphData, anchorScores, baseline, onQuestionClick }) => {
  if (!graphData || graphData.length === 0) {
    return (
      <div className="full-survey-graph-container">
        <p className="no-data-message">No answer data available for graph visualization.</p>
      </div>
    );
  }

  // Check if answers are numeric (prediction survey) or text-based (RSJT)
  const isNumericSurvey = graphData.length > 0 && !isNaN(parseFloat(graphData[0].answer));
  
  // Prepare data for chart (with question number as x-axis)
  const chartData = graphData.map((point, index) => {
    const answerValue = isNumericSurvey ? (parseFloat(point.answer) || 0) : index; // Use index for text answers
    return {
      questionNumber: index + 1,
      questionId: point.question_id,
      answer: answerValue,
      answerText: point.answer, // Store original answer text
      anchor: point.anchor,
      anchorName: point.anchor_name || (point.anchor ? getAnchorName(point.anchor) : null),
      questionText: point.question_text,
      sequence: point.sequence || index + 1,
      // EEG band data (if available)
      delta: point.delta || null,
      theta: point.theta || null,
      alpha: point.alpha || null,
      beta: point.beta || null,
      gamma: point.gamma || null
    };
  });
  
  // Check if we have EEG band data
  const hasEEGData = chartData.some(d => d.alpha !== null || d.beta !== null || d.delta !== null || d.theta !== null || d.gamma !== null);

  // Calculate overall average (only for numeric surveys)
  const overallAvg = isNumericSurvey 
    ? chartData.reduce((sum, d) => sum + d.answer, 0) / chartData.length 
    : null;

  // Get anchor boundaries for background coloring (only for numeric surveys with anchors)
  const anchorBoundaries = [];
  if (isNumericSurvey) {
    let currentAnchor = null;
    chartData.forEach((point, index) => {
      if (point.anchor !== currentAnchor) {
        if (currentAnchor !== null) {
          anchorBoundaries[anchorBoundaries.length - 1].end = index;
        }
        anchorBoundaries.push({
          anchor: point.anchor,
          start: index,
          end: chartData.length - 1
        });
        currentAnchor = point.anchor;
      }
    });
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="graph-tooltip">
          <p className="tooltip-question">Q{data.sequence}: {data.questionText}</p>
          <p className="tooltip-answer">
            Answer: {isNumericSurvey ? `${data.answer.toFixed(1)}/10` : data.answerText}
          </p>
          {data.anchorName && (
            <p className="tooltip-anchor">Anchor: {data.anchorName}</p>
          )}
          {hasEEGData && (
            <div className="tooltip-eeg-bands">
              <p><strong>EEG Bands:</strong></p>
              {data.delta !== null && <p>Delta: {data.delta.toFixed(1)}%</p>}
              {data.theta !== null && <p>Theta: {data.theta.toFixed(1)}%</p>}
              {data.alpha !== null && <p>Alpha: {data.alpha.toFixed(1)}%</p>}
              {data.beta !== null && <p>Beta: {data.beta.toFixed(1)}%</p>}
              {data.gamma !== null && <p>Gamma: {data.gamma.toFixed(1)}%</p>}
              {baseline && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #ccc' }}>
                  <p><strong>Baseline:</strong></p>
                  <p style={{ fontSize: '0.85em' }}>Δ: {baseline.delta?.toFixed(1)}% | θ: {baseline.theta?.toFixed(1)}% | α: {baseline.alpha?.toFixed(1)}% | β: {baseline.beta?.toFixed(1)}% | γ: {baseline.gamma?.toFixed(1)}%</p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="full-survey-graph-container">
      <h3 className="graph-title">Full Survey Answer Pattern {hasEEGData && 'with EEG Bands'}</h3>
      <p className="graph-description">
        This graph shows all your answers across the survey. Each point represents your answer (0-10 scale) for a question.
        {hasEEGData && ' The colored lines show EEG frequency bands (alpha, beta, delta, theta, gamma) for each question.'}
        Questions are grouped by resilience anchors (Identity, Future Orientation, Ability, Execution, Social Immunity).
      </p>
      
      <div className="graph-wrapper">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            onClick={(data) => {
              console.log('[FullSurveyAnswerGraph] LineChart onClick triggered:', data);
              if (data && data.activeIndex !== undefined && onQuestionClick) {
                const index = parseInt(data.activeIndex);
                const clickedData = chartData[index];
                console.log('[FullSurveyAnswerGraph] Clicked data at index:', index, clickedData);
                if (clickedData && clickedData.questionId) {
                  console.log('[FullSurveyAnswerGraph] Calling onQuestionClick with questionId:', clickedData.questionId);
                  onQuestionClick(clickedData.questionId);
                } else {
                  console.log('[FullSurveyAnswerGraph] No questionId in clicked data');
                }
              } else {
                console.log('[FullSurveyAnswerGraph] Missing data or onQuestionClick:', { 
                  hasData: !!data, 
                  hasActiveIndex: data?.activeIndex !== undefined,
                  hasOnQuestionClick: !!onQuestionClick 
                });
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey="questionNumber" 
              label={{ value: 'Question Number', position: 'insideBottom', offset: -10 }}
              tick={{ fontSize: 12 }}
            />
            {isNumericSurvey ? (
              <YAxis 
                yAxisId="answers"
                domain={[0, 10]}
                label={{ value: 'Answer Score (0-10)', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
            ) : (
              <YAxis 
                yAxisId="answers"
                hide={true}
              />
            )}
            {hasEEGData && (
              <YAxis 
                yAxisId="eeg"
                orientation="right"
                domain={[0, 100]}
                label={{ value: 'EEG Band %', angle: 90, position: 'insideRight' }}
                tick={{ fontSize: 12 }}
              />
            )}
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {/* Average line (only for numeric surveys) */}
            {isNumericSurvey && overallAvg !== null && (
              <ReferenceLine 
                yAxisId="answers"
                y={overallAvg} 
                stroke="#666" 
                strokeDasharray="5 5" 
                label={{ value: `Average: ${overallAvg.toFixed(1)}`, position: 'right' }}
              />
            )}
            
            {/* Answer line - use Scatter for text answers, Line for numeric */}
            {isNumericSurvey ? (
              <Line 
                yAxisId="answers"
                type="monotone" 
                dataKey="answer" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (!payload) return null;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill="#8B5CF6"
                      cursor="pointer"
                      style={{ pointerEvents: 'all' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('[FullSurveyAnswerGraph] Dot clicked, payload:', payload);
                        if (onQuestionClick && payload.questionId) {
                          console.log('[FullSurveyAnswerGraph] Calling onQuestionClick with:', payload.questionId);
                          onQuestionClick(payload.questionId);
                        }
                      }}
                    />
                  );
                }}
                activeDot={{ r: 7, cursor: 'pointer' }}
                name="Your Answer"
              />
            ) : (
              <Scatter
                yAxisId="answers"
                dataKey="answer"
                fill="#8B5CF6"
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  if (!payload) return null;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill="#8B5CF6"
                      cursor="pointer"
                      style={{ pointerEvents: 'all' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onQuestionClick && payload.questionId) {
                          onQuestionClick(payload.questionId);
                        }
                      }}
                    />
                  );
                }}
                name="Your Answer"
              />
            )}
            
            {/* EEG Band lines */}
            {hasEEGData && (
              <>
                <Line 
                  yAxisId="eeg"
                  type="monotone" 
                  dataKey="delta" 
                  stroke="#EF4444" 
                  strokeWidth={1.5}
                  strokeDasharray="2 2"
                  dot={false}
                  name="Delta"
                  connectNulls
                />
                <Line 
                  yAxisId="eeg"
                  type="monotone" 
                  dataKey="theta" 
                  stroke="#F59E0B" 
                  strokeWidth={1.5}
                  strokeDasharray="2 2"
                  dot={false}
                  name="Theta"
                  connectNulls
                />
                <Line 
                  yAxisId="eeg"
                  type="monotone" 
                  dataKey="alpha" 
                  stroke="#10B981" 
                  strokeWidth={1.5}
                  dot={false}
                  name="Alpha"
                  connectNulls
                />
                <Line 
                  yAxisId="eeg"
                  type="monotone" 
                  dataKey="beta" 
                  stroke="#3B82F6" 
                  strokeWidth={1.5}
                  dot={false}
                  name="Beta"
                  connectNulls
                />
                <Line 
                  yAxisId="eeg"
                  type="monotone" 
                  dataKey="gamma" 
                  stroke="#8B5CF6" 
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                  name="Gamma"
                  connectNulls
                />
                
                {/* Baseline reference lines */}
                {baseline && (
                  <>
                    <ReferenceLine 
                      yAxisId="eeg"
                      y={baseline.delta} 
                      stroke="#EF4444" 
                      strokeDasharray="1 1" 
                      strokeOpacity={0.3}
                      label={{ value: `Δ baseline`, position: 'right', fontSize: 10 }}
                    />
                    <ReferenceLine 
                      yAxisId="eeg"
                      y={baseline.theta} 
                      stroke="#F59E0B" 
                      strokeDasharray="1 1" 
                      strokeOpacity={0.3}
                      label={{ value: `θ baseline`, position: 'right', fontSize: 10 }}
                    />
                    <ReferenceLine 
                      yAxisId="eeg"
                      y={baseline.alpha} 
                      stroke="#10B981" 
                      strokeDasharray="1 1" 
                      strokeOpacity={0.3}
                      label={{ value: `α baseline`, position: 'right', fontSize: 10 }}
                    />
                    <ReferenceLine 
                      yAxisId="eeg"
                      y={baseline.beta} 
                      stroke="#3B82F6" 
                      strokeDasharray="1 1" 
                      strokeOpacity={0.3}
                      label={{ value: `β baseline`, position: 'right', fontSize: 10 }}
                    />
                    <ReferenceLine 
                      yAxisId="eeg"
                      y={baseline.gamma} 
                      stroke="#8B5CF6" 
                      strokeDasharray="1 1" 
                      strokeOpacity={0.3}
                      label={{ value: `γ baseline`, position: 'right', fontSize: 10 }}
                    />
                  </>
                )}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Anchor scores summary */}
      {anchorScores && (
        <div className="anchor-scores-summary">
          <h4>Resilience Anchor Scores</h4>
          <div className="anchor-scores-grid">
            {Object.entries(anchorScores).map(([anchor, data]) => (
              <div key={anchor} className="anchor-score-card" style={{ borderLeftColor: getAnchorColor(anchor) }}>
                <div className="anchor-score-name">{getAnchorName(anchor)}</div>
                <div className="anchor-score-value">{data.average.toFixed(1)}/10</div>
                <div className="anchor-score-classification">{data.classification}</div>
                <div className="anchor-score-count">{data.count} questions</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FullSurveyAnswerGraph;
